/**
 * WaitingRoom
 *
 * Handles the callback flow after biometric (BioFace) authentication.
 * This component polls the backend for login status using `postCheckLogin`.
 *
 * Note:
 * Backend can return HTTP 202 (Accepted) when the biometric provider
 * has not yet returned a final response. In this case, polling continues
 * until a final status is received or the max attempt limit is reached.
 *
 * Flow:
 * - 202 → verification in progress → continue polling
 * - Success → proceed with login / next step (e.g. SMS verification)
 * - Failure or timeout → clear session and redirect to login
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PageLoader } from 'src/Components';
import { routerPath } from 'src/Config';
import { UACActions, UACConstants } from 'src/Redux';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

export const WaitingRoom = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const isMountedRef = useRef(true);

  const Language = useSelector((state: any) => state.languageReducer?.language);

  const doLogin = useCallback(async () => {
    const LoginCode = window.sessionStorage.getItem('loginCode');
    const LoginToken = window.sessionStorage.getItem('loginToken');

    if (!LoginCode || !LoginToken) {
      navigate(routerPath.login, { replace: true });
      return;
    }

    const result = await dispatch<any>(
      UACActions.postCheckLogin({
        Language,
        LoginCode,
        LoginSystem: 'BioFace',
        LoginToken,
      })
    );

    if (!isMountedRef.current) return;

    if (result?.payload?.responseStatus === 202) {
      attemptsRef.current += 1;

      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        sessionStorage.removeItem('loginCode');
        sessionStorage.removeItem('loginToken');
        navigate(routerPath.login, { replace: true });

        return;
      }

      pollTimer.current = setTimeout(doLogin, POLL_INTERVAL_MS);
      return;
    }

    sessionStorage.removeItem('loginCode');
    sessionStorage.removeItem('loginToken');

    if (result?.type === UACConstants.POST_UAC_CHECK_LOGIN_SUCCESS) {
      if (result.payload?.LoginSystem === 'Sms') {
        navigate(routerPath.verification, {
          replace: true,
          state: {
            LoginCode,
            LoginToken: result.payload.RefreshToken,
            LoginAuthCode: null,
            LoginSystem: result.payload.LoginSystem,
            Language,
            LoginStatusCode: result.payload.LoginStatusCode,
          },
        });
      } else {
        navigate(routerPath.index, { replace: true });
      }

      return;
    }

    navigate(routerPath.login, { replace: true });
  }, [Language, dispatch, navigate]);

  useEffect(() => {
    isMountedRef.current = true;
    doLogin();

    return () => {
      isMountedRef.current = false;

      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
    };
  }, [doLogin]);

  return <PageLoader />;
};
